import { useLazyQuery } from '@apollo/client';
import { FC, useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import CLOSE_ICON from '../../assets/images/close.svg';
import Loader from '../../components/layout/Loader';
import { AppContext } from '../../context';
import { ISheetColumn } from '../../utils/commissionSheet';
import { DIRECT_HIRE, ROLES } from '../../utils/constant';
import { copySheetToClipboard } from '../../utils/sheetClipboard';
import { GET_DH_COMMISSION_SHEET_PLACEMENTS } from './gql';

// kan-98: the executive works the dashboard a page at a time and pastes whole
// blocks of rows into his external sheets. Every one of those exports is the
// same modal over the same batch query — only the columns differ — so this
// component owns the query, the table and the clipboard, and each button
// supplies its own row builder.

export const NO_DH_TITLE = 'No direct-hire placements on this page';

/** Why a direct-hire placement didn't make it into the sheet. */
export type TSheetSkipReason = 'commissions' | 'payment';

/** The flattened shape dhCommissionSheetPlacements returns per placement. */
export interface IDhSheetPlacement {
  id: string;
  placementId?: number | null;
  agreementNickName?: string | null;
  splits: Array<any>;
  commissions: Array<any>;
  payments?: Array<any>;
}

export interface IDhSheetRow {
  columns: Array<ISheetColumn>;
  /** False leaves the placement out of the sheet (and counts a skip). */
  include: boolean;
  skipReason?: TSheetSkipReason;
}

interface IDhSheetBulkExportProps {
  buttonLabel: string;
  modalTitle: string;
  /** The rows currently loaded in the listing (whatever filters/page apply). */
  placements: Array<any>;
  buildRow: (placement: IDhSheetPlacement) => IDhSheetRow;
  /** Shown when the page has direct-hire rows but none of them qualified. */
  emptyMessage: string;
  /** Per-reason muted note above the table, e.g. "2 left out — ...". */
  skipNotes: Partial<Record<TSheetSkipReason, (count: number) => string>>;
}

/** The batch query flattens what the builders read as nested query shapes. */
export const sheetSplitting = (placement: IDhSheetPlacement): Array<any> =>
  (placement?.splits || []).map((split: any) => ({
    splitType: split?.splitType,
    splitPercentage: split?.splitPercentage,
    user: { id: split?.userId, fullName: split?.userFullName },
  }));

/** The builders take a GET_PLACEMENT_BY_ID-shaped placement. */
export const sheetPlacement = (placement: IDhSheetPlacement): any => ({
  ...placement,
  agreement: { nickName: placement?.agreementNickName },
});

const SKIP_ORDER: Array<TSheetSkipReason> = ['commissions', 'payment'];

const DhSheetBulkExport: FC<IDhSheetBulkExportProps> = ({
  buttonLabel,
  modalTitle,
  placements,
  buildRow,
  emptyMessage,
  skipNotes,
}) => {
  const { userRoles } = useContext(AppContext);
  const isExecutive = !!userRoles?.includes(ROLES.EXECUTIVE);

  const [open, setOpen] = useState<boolean>(false);

  const [fetchSheetPlacements, { data, loading, error }] = useLazyQuery(
    GET_DH_COMMISSION_SHEET_PLACEMENTS
  );

  // Page order is the sheet's order, so the ids go to the server as listed.
  const dhIds: Array<string> = useMemo(
    () =>
      (placements || [])
        .filter((item: any) => item?.placementType === DIRECT_HIRE)
        .map((item: any) => item?.id)
        .filter(Boolean),
    [placements]
  );

  const built: Array<{ placement: IDhSheetPlacement; row: IDhSheetRow }> =
    useMemo(
      () =>
        ((data?.dhCommissionSheetPlacements || []) as Array<IDhSheetPlacement>).map(
          (placement) => ({ placement, row: buildRow(placement) })
        ),
      [data, buildRow]
    );

  const rows = built.filter(({ row }) => row.include);

  // Counted per reason so the executive knows what to go fix.
  const skipCounts = built.reduce((counts, { row }) => {
    if (row.include || !row.skipReason) return counts;
    counts[row.skipReason] = (counts[row.skipReason] || 0) + 1;
    return counts;
  }, {} as Record<string, number>);
  const skippedCount = built.length - rows.length;

  if (!isExecutive) return null;

  const toggle = () => {
    if (!open) {
      // Always current — the executive opens this right after editing splits
      // or commissions in another tab.
      fetchSheetPlacements({
        variables: { placementIds: dhIds },
        fetchPolicy: 'network-only',
      });
    }
    setOpen(!open);
  };

  const headerColumns = rows[0]?.row?.columns || [];

  const copyAll = async () => {
    // No cell is colored, so the shared helper stays plain-text only and the
    // destination sheet's own number formats keep applying on paste.
    const copied = await copySheetToClipboard(
      rows.map(({ row }) => row.columns.map((column) => ({ text: column.value })))
    );
    if (copied) {
      toast.success(`${rows.length} rows copied — paste into the sheet`);
    } else {
      toast.error('Copy failed — please copy the values manually');
    }
  };

  return (
    <>
      <button
        type="button"
        className="buttonGenericStyle filledButton mr-3"
        onClick={toggle}
        disabled={!dhIds.length}
        style={{ opacity: dhIds.length ? 1 : 0.5 }}
        title={dhIds.length ? undefined : NO_DH_TITLE}
      >
        {buttonLabel}
      </button>

      <Modal
        isOpen={open}
        toggle={toggle}
        size="xl"
        className="revampDialog"
        style={{ maxWidth: '95vw' }}
      >
        <ModalHeader toggle={toggle}>
          <div className="d-flex align-items-center">
            <span>{modalTitle}</span>
          </div>
          <button className="iconButton" onClick={toggle} type="button">
            <img src={CLOSE_ICON} alt="close" />
          </button>
        </ModalHeader>

        <ModalBody>
          {loading && <Loader loaderClass="sm-height" />}

          {!loading && error && (
            <p className="text-danger m-0">
              {error?.message || 'Unable to load the rows'}
            </p>
          )}

          {!loading && !error && !rows.length && (
            <p className="text-muted m-0">
              {skippedCount ? emptyMessage : NO_DH_TITLE}
            </p>
          )}

          {!loading &&
            !error &&
            !!rows.length &&
            SKIP_ORDER.map((reason) => {
              const count = skipCounts[reason] || 0;
              const note = skipNotes[reason];
              if (!count || !note) return null;
              return (
                <p
                  key={reason}
                  className="text-muted mb-2"
                  style={{ fontSize: '12px' }}
                >
                  {note(count)}
                </p>
              );
            })}

          {!loading && !error && !!rows.length && (
            // One row per placement, like the sheet itself, scrollable sideways.
            <div style={{ overflowX: 'auto' }}>
              <Table
                bordered
                size="sm"
                style={{ width: 'max-content', fontSize: '12px' }}
              >
                <thead>
                  <tr>
                    {/* Not part of the copy — it only tells the executive
                        which placement each row came from. */}
                    <th style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}>
                      <small className="text-muted d-block font-weight-normal">
                        &nbsp;
                      </small>
                      Placement #
                    </th>
                    {headerColumns.map((column) => (
                      <th
                        key={column.letter}
                        style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}
                      >
                        <small className="text-muted d-block font-weight-normal">
                          {column.letter}
                        </small>
                        {column.name || <>&nbsp;</>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ placement, row }) => (
                    <tr key={placement?.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {placement?.placementId ?? (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      {row.columns.map((column) => {
                        const shown = column.display ?? column.value;
                        return (
                          <td key={column.letter} style={{ whiteSpace: 'nowrap' }}>
                            {shown || <span className="text-muted">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <button type="button" onClick={toggle} className="buttonPrimary mr-3">
            Close
          </button>
          <button
            type="button"
            onClick={copyAll}
            disabled={!rows.length}
            className="buttonGenericStyle filledButton"
          >
            Copy All
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DhSheetBulkExport;
